import { useEffect, useId, useState, type CSSProperties } from "react";
import { flagIngredientOut } from "../lib/api";
import { useDialogA11y } from "../lib/useDialogA11y";

interface Props {
  open: boolean;
  ingredientId: string;
  ingredientName: string;
  onClose: () => void;
  onFlagged: (msg: string) => void;
}

/**
 * Bartender confirmation modal for flagging an ingredient out-of-stock.
 * Optional note field; submit fires `bar-flag-ingredient-out` which:
 *   - Sets ingredients.available = false
 *   - Optionally cascades 86 (per app_settings.auto_86_on_unavailable_ingredient)
 *   - Sends SMS to every bar_owner + bar_manager with a phone
 *
 * Authorization: any bar role (this is the service-floor escape valve —
 * bartenders must be able to mark an ingredient out without finding a manager).
 */
export default function FlagIngredientModal({
  open,
  ingredientId,
  ingredientName,
  onClose,
  onFlagged,
}: Props) {
  const titleId = useId();
  const noteId = useId();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { dialogRef } = useDialogA11y(open, onClose, !busy);

  useEffect(() => {
    if (open) {
      setNote("");
      setBusy(false);
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const r = await flagIngredientOut({
        ingredient_id: ingredientId,
        note: note.trim() || undefined,
      });
      const delivered = r.sms?.delivered ?? 0;
      const cascadeNote = r.cascade_applied
        ? r.cascaded_drink_count > 0
          ? ` · ${r.cascaded_drink_count} ${r.cascaded_drink_count === 1 ? "drink" : "drinks"} auto-86'd`
          : ""
        : "";
      onFlagged(
        `Flagged ${r.ingredient_name} out${cascadeNote}. ${delivered} owner${
          delivered === 1 ? "" : "s"
        }/manager${delivered === 1 ? "" : "s"} notified.`,
      );
      onClose();
    } catch (err: any) {
      setError(err?.message ?? "Could not flag");
      setBusy(false);
    }
  }

  return (
    <div style={styles.backdrop} onClick={busy ? undefined : onClose}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={styles.dialog}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id={titleId} style={styles.title}>Mark out of stock</h2>
        <p style={styles.body}>
          <strong>{ingredientName}</strong> will be flagged unavailable. Owners
          and managers get an SMS so they can verify or source a substitute.
        </p>
        <p style={styles.subtle}>
          Affected drinks may auto-86 if the owner has it enabled in Settings.
          Otherwise managers will see the unavailable flag and decide.
        </p>

        <label htmlFor={noteId} style={styles.label}>
          Note <span style={styles.req}>(optional)</span>
        </label>
        <textarea
          id={noteId}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder='e.g. "Last bottle just emptied" or "Missing from delivery"'
          style={styles.textarea}
          rows={3}
          autoFocus
          disabled={busy}
        />

        {error && <div style={styles.error} role="alert">{error}</div>}

        <div style={styles.actions}>
          <button type="button" onClick={onClose} disabled={busy} style={styles.cancelBtn}>
            Cancel
          </button>
          <button type="button" onClick={submit} disabled={busy} style={styles.confirmBtn}>
            {busy ? "Flagging…" : "Mark out of stock"}
          </button>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.65)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 99,
  },
  dialog: {
    width: "100%",
    maxWidth: 460,
    maxHeight: "90vh",
    overflowY: "auto",
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: 16,
    padding: 20,
    boxShadow: "0 8px 28px rgba(0,0,0,0.5)",
  },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 22,
    margin: "0 0 8px",
    color: "var(--text)",
  },
  body: { margin: "0 0 8px", fontSize: 13, color: "var(--text)", lineHeight: 1.5 },
  subtle: { margin: "0 0 12px", fontSize: 12, color: "var(--muted)", lineHeight: 1.4 },
  label: {
    display: "block",
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
    color: "var(--muted)",
    marginTop: 12,
    marginBottom: 6,
  },
  req: { color: "var(--muted)", textTransform: "none", letterSpacing: 0 },
  textarea: {
    width: "100%",
    boxSizing: "border-box",
    background: "var(--bg)",
    color: "var(--text)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: 10,
    fontSize: 16,
    fontFamily: "inherit",
    resize: "vertical",
  },
  error: {
    marginTop: 10,
    padding: 10,
    background: "rgba(224,80,64,0.1)",
    border: "1px solid rgba(224,80,64,0.4)",
    color: "#FF8A7A",
    borderRadius: 10,
    fontSize: 12,
  },
  actions: {
    display: "flex",
    gap: 8,
    justifyContent: "flex-end",
    marginTop: 16,
  },
  cancelBtn: {
    background: "transparent",
    color: "var(--muted)",
    border: "1px solid var(--border)",
    borderRadius: 10,
    padding: "10px 16px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 40,
  },
  confirmBtn: {
    background: "rgba(224,80,64,0.2)",
    color: "#FF8A7A",
    border: "1px solid rgba(224,80,64,0.5)",
    borderRadius: 10,
    padding: "10px 16px",
    fontSize: 12,
    fontWeight: 800,
    cursor: "pointer",
    minHeight: 40,
  },
};
